import { AggregateFunctionName, RunDto } from "@custom-types/modelRun"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import { graphSampleNodeToViewGroupNodeLabel } from "@features/graph/utils/graphUtils"
import { AggregateFunctionChart } from "@features/results/Result/AggregateFunctionChart"
import { DashboardChartContainer } from "@features/results/Result/Dashboard/DashboardChartContainer"
import {
  ChartGroupLayout,
  DashboardAggregateChartDto,
} from "@features/results/Result/Dashboard/dashboardModel"
import { useDashboardConfig } from "@features/results/Result/Dashboard/dashboardConfig"
import { useIntersectionObserver } from "@hooks/useIntersectionObserver"
import { useRef } from "react"

export const DashboardAggregateChart = (props: {
  chart: DashboardAggregateChartDto
  runs: RunDto[]
  layout?: ChartGroupLayout
}) => {
  const [cfg, updateCfg] = useDashboardConfig({
    showLegend: true,
    // "Node" and "NodeSelect" shouldn't be used at the same time, but just in case,
    // we prioritize "NodeSelect" as it is more specific and allows to define different aggregates for different nodes
    selectedNodes: props.chart.NodeSelect?.[0]
      ? props.chart.NodeSelect[0].Nodes || []
      : props.chart.Nodes || [],
    selectedAggregate: props.chart.NodeSelect?.[0]
      ? props.chart.NodeSelect[0].Aggregate || []
      : props.chart.Aggregate || [],
    hiddenDatasets: [],
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const isVisible = useIntersectionObserver(containerRef)

  const selectedNodes = cfg.selectedNodes ?? []
  const selectedAggregate = cfg.selectedAggregate ?? []

  const handleShowLegend = () => {
    updateCfg((draft) => {
      draft.showLegend = !draft.showLegend
    })
  }

  const handleOptionChange = (nodes: string[], aggregate: string[]) => {
    updateCfg((draft) => {
      draft.selectedNodes = nodes
      draft.selectedAggregate = aggregate
    })
  }

  const handleLegendClick = (datasetLabel: string) => {
    updateCfg((draft) => {
      const hiddenDatasets = draft.hiddenDatasets ?? []
      if (hiddenDatasets.includes(datasetLabel)) {
        draft.hiddenDatasets = hiddenDatasets.filter((label) => label !== datasetLabel)
      } else {
        draft.hiddenDatasets = [...hiddenDatasets, datasetLabel]
      }
    })
  }

  const handleFullscreen = () => {
    updateCfg((draft) => {
      draft.isFullscreen = !draft.isFullscreen
    })
  }

  const nodeLabels: GroupNodeLabel[] = selectedNodes.map(graphSampleNodeToViewGroupNodeLabel)
  const runIds = props.runs.map((run) => run.key)

  return (
    <DashboardChartContainer
      chart={props.chart}
      layout={props.layout}
      onOptionChange={(v) => handleOptionChange(v.Nodes, v.Aggregate)}
      onFullScreen={handleFullscreen}
      runIds={runIds}
      onShowLegendToggle={handleShowLegend}
      showLegend={cfg.showLegend}
      ref={containerRef}
    >
      {isVisible && (
        <AggregateFunctionChart
          runs={props.runs}
          node={nodeLabels[0] ?? null}
          aggregationFunc={selectedAggregate as AggregateFunctionName[]}
          ref={undefined}
          customLabels={selectedNodes}
          maintainAspectRatio={false}
          sx={{ height: "100%" }}
          chartContainerSx={{ height: "100%" }}
          legendPosition={props.chart.LegendPosition}
          showLegend={cfg.showLegend}
          hiddenDatasets={cfg.hiddenDatasets}
          onLegendClick={handleLegendClick}
        />
      )}
    </DashboardChartContainer>
  )
}
